import { useState } from 'react'
import { useForm } from 'react-hook-form'
import HomestayFooterCmp from '@/components/custom/ui/clients/tempula/homestay/homestayFooter'
import HomestayNavbar from '@/components/custom/ui/clients/tempula/homestay/homestayNavbar'
import HomestayfooterConfig from '@/constant/config/clients/tempula/homestay/homestayFooterConfig'
import HomenavConfig from '@/constant/config/clients/tempula/homestay/homestayNavConfig'
import { createFileRoute } from '@tanstack/react-router'
import { contactApi } from '@/lib/api'
import { SEO, buildPageHead } from '@/constant/config/clients/tempula/seo'

export const Route = createFileRoute('/_clients/tempula/contact')({
    head: () => buildPageHead(SEO.contact),
    component: RouteComponent,
})

type FormData = {
  name: string
  phone: string
  email: string
  message: string
}

function RouteComponent() {
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const onSubmit = async (data: FormData) => {
    setSubmitError(null)
    try {
      // API expects full international format e.g. +919876543210
      const phone = data.phone.startsWith("+") ? data.phone : `+91${data.phone}`
      await contactApi.submit({
        name:    data.name,
        phone,
        email:   data.email || undefined,
        message: data.message,
      })
      setSubmitted(true)
    } catch {
      setSubmitError("Something went wrong. Please try again.")
    }
  }

  const inputCls = (hasError: boolean) =>
    `w-full bg-myGreenBlue/50 border ${
      hasError ? "border-red-500/70" : "border-white/10"
    } rounded-xl px-4 py-3 text-white placeholder:text-white/25 focus:outline-none focus:border-myBlue focus:ring-2 focus:ring-myBlue/30 transition-all duration-200`

  return (
    <div className="relative min-h-screen bg-BgGallery flex flex-col">
      <HomestayNavbar {...HomenavConfig} />

      <div className="flex-1 flex items-center justify-center p-4 md:p-8 pt-32 md:pt-36">
        <div className="w-full max-w-xl">
          <div className="bg-myNav border border-white/5 rounded-2xl p-8 shadow-2xl">

            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
              <span className="h-7 w-1.5 bg-myBlue rounded-full" />
              <h1 className="text-2xl font-bold text-white tracking-wide">Get in Touch</h1>
            </div>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-myBlue/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-myBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-white text-xl font-semibold mb-2">Message Sent!</h2>
                <p className="text-white/50 text-sm">We'll get back to you as soon as possible.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest block mb-1.5">Full Name</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    {...register("name", { required: "Name is required" })}
                    className={inputCls(!!errors.name)}
                  />
                  {errors.name && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.name.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest block mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    placeholder="10-digit number"
                    {...register("phone", {
                      required: "Phone number is required",
                      pattern: { value: /^[0-9]{10}$/, message: "Must be exactly 10 digits" },
                    })}
                    className={inputCls(!!errors.phone)}
                  />
                  {errors.phone && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.phone.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest block mb-1.5">Email Address <span className="text-white/20 normal-case">(optional)</span></label>
                  <input
                    type="email"
                    placeholder="john@example.com"
                    {...register("email", {
                      pattern: { value: /\S+@\S+\.\S+/, message: "Invalid email format" },
                    })}
                    className={inputCls(!!errors.email)}
                  />
                  {errors.email && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-white/40 text-xs uppercase tracking-widest block mb-1.5">Message</label>
                  <textarea
                    rows={4}
                    placeholder="How can we help you?"
                    {...register("message", { required: "Message is required" })}
                    className={`${inputCls(!!errors.message)} resize-none`}
                  />
                  {errors.message && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <span>⚠</span> {errors.message.message}
                    </p>
                  )}
                </div>

                {submitError && (
                  <p className="text-red-400 text-sm text-center">{submitError}</p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-myBlue hover:bg-myBlueHover active:scale-[0.99] text-white text-lg font-semibold py-4 rounded-xl cursor-pointer transition-all duration-200 shadow-lg shadow-myBlue/20 tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <HomestayFooterCmp {...HomestayfooterConfig} />
    </div>
  )
}
